import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import AppointmentNotesModal from '../components/AppointmentNotesModal';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

const RELATION_OPTIONS = [
  { value: 'Father', label: 'Father' },
  { value: 'Mother', label: 'Mother' },
  { value: 'Son', label: 'Son' },
  { value: 'Daughter', label: 'Daughter' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Brother', label: 'Brother' },
  { value: 'Sister', label: 'Sister' },
  { value: 'Other', label: 'Other' },
];

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

function UserFamily() {
  const navigate = useNavigate();
  const [family, setFamily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    relation: '',
    gender: '',
    age: '',
    phone_number: '',
    medical_history: '',
    medical_record: null
  });

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const res = await api.get('user/session-check/');
      if (!res.data.authenticated) {
        navigate('/user/login');
        return;
      }
      fetchFamily();
      loadDoctors();
    } catch (err) {
      console.error("Session check failed:", err);
      navigate('/user/login');
    }
  };

  const fetchFamily = async () => {
    try {
      const res = await api.get('user/family/');
      setFamily(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/user/login');
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      const res = await api.get('user/doctor_list/');
      setDoctors(res.data);
    } catch (err) {
      console.error("Failed to load doctors:", err);
    }
  };

  const handleAddFamilyMember = async (e) => {
    e.preventDefault();
    if (!formData.relation) {
      toast.warning('Please select a relation.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }
    if (!formData.gender) {
      toast.warning('Please select a gender.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          if (key === 'medical_record' && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else if (key !== 'medical_record') {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      await api.post(
        'user/family/add/',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setShowAddModal(false);
      setFormData({
        name: '',
        relation: '',
        gender: '',
        age: '',
        phone_number: '',
        medical_history: '',
        medical_record: null
      });
      fetchFamily();
      toast.success('Family member added successfully!', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Failed to add family member:", err);
      toast.error(err.response?.data?.error || err.response?.data?.msg || "Failed to add family member", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleSelectDoctor = async (doctorId) => {
    setSelectedDoctor(doctorId);
    try {
      const res = await api.get(`appointments/user/slots/${doctorId}/`);
      setSlots(res.data);
    } catch (err) {
      console.error("Failed to load slots:", err);
      toast.error("Failed to load available slots", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleBookAppointment = () => {
    if (!selectedSlot || !selectedMember) {
      toast.warning("Please select a slot", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    // Same as self booking: notes/report first, then booking summary → payment
    setShowBookingModal(false);
    setShowNotesModal(true);
  };

  const handleNotesProceed = ({ patientNotes, medicalReportFiles, medicalReportFile }) => {
    setShowNotesModal(false);
    if (!selectedSlot || !selectedMember) return;
    const files = Array.isArray(medicalReportFiles) && medicalReportFiles.length
      ? medicalReportFiles
      : (medicalReportFile ? [medicalReportFile] : []);

    navigate(
      `/user/booking-summary?slot_id=${selectedSlot.id}&family_member_id=${selectedMember.id}`,
      {
        state: {
          patientNotes,
          medicalReportFiles: files,
          medicalReportFile: files[0] || null,
          backTo: '/user/family',
          familyMember: {
            id: selectedMember.id,
            name: selectedMember.name,
            relation: selectedMember.relation,
          },
        },
      }
    );
  };

  const resetBookingState = () => {
    setShowBookingModal(false);
    setShowNotesModal(false);
    setSelectedMember(null);
    setSelectedDoctor(null);
    setSelectedSlot(null);
    setSlots([]);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      relation: member.relation || '',
      gender: member.gender || '',
      age: member.age || '',
      phone_number: member.phone_number || '',
      medical_history: member.medical_history || '',
      medical_record: null
    });
    setShowEditModal(true);
  };

  const handleUpdateFamilyMember = async (e) => {
    e.preventDefault();
    if (!formData.relation) {
      toast.warning('Please select a relation.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }
    if (!formData.gender) {
      toast.warning('Please select a gender.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }
    setSaving(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          if (key === 'medical_record' && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else if (key !== 'medical_record') {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      await api.patch(
        `user/family/${editingMember.id}/update/`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setShowEditModal(false);
      setEditingMember(null);
      setFormData({
        name: '',
        relation: '',
        gender: '',
        age: '',
        phone_number: '',
        medical_history: '',
        medical_record: null
      });
      fetchFamily();
      toast.success('Family member updated successfully!', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Failed to update family member:", err);
      toast.error(err.response?.data?.error || err.response?.data?.msg || "Failed to update family member", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (memberId) => {
    setMemberToDelete(memberId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;

    try {
      await api.delete(`user/family/${memberToDelete}/delete/`);
      fetchFamily();
      toast.success('Family member deleted successfully!', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Failed to delete family member:", err);
      toast.error(err.response?.data?.error || "Failed to delete family member", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setShowDeleteConfirm(false);
      setMemberToDelete(null);
    }
  };

  const getMedicalRecordUrl = (record) => {
    if (!record) return null;
    if (record.startsWith('http://') || record.startsWith('https://')) {
      return record;
    }
    return `${process.env.REACT_APP_API_URL}${record}`;
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <style>{`
        .action-btn-book:hover {
          text-decoration: underline;
          text-decoration-color: #0d6efd;
          text-underline-offset: 4px;
        }
        .action-btn-edit:hover {
          text-decoration: underline;
          text-decoration-color:rgb(254, 191, 0);
          text-underline-offset: 4px;
        }
        

 
/* Default style: looks like normal text */
.action-btn-delete {
  background-color: transparent;
  color: #dc3545; /* red text */
  border: none;
  border-radius: 50px; /* pill shape */
  padding: 4px 12px; /* adjust size */
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

/* Hover style: red pill background and white text */
.action-btn-delete:hover {
  background-color: #dc3545; /* red background */
  color: #fff; /* white text */
}



      `}</style>
      <UserNavbar />
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold text-dark mb-0">
              <i className="bi bi-people-fill me-2 text-primary"></i>Family Members
            </h2>
            <p className="text-muted">Manage your family members and book appointments for them</p>
          </div>
          <button
            className="btn btn-primary rounded-pill px-4 shadow-sm"
            onClick={() => setShowAddModal(true)}
          >
            <i className="bi bi-person-plus me-2"></i>Add Family Member
          </button>
        </div>

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4">
            {family.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-people fs-1 text-muted mb-3 d-block"></i>
                <p className="text-muted">No family members added yet.</p>
                <button
                  className="btn btn-primary rounded-pill px-4"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Your First Family Member
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light text-center">
                    <tr>
                      <th>Name</th>
                      <th>Relation</th>
                      <th>Gender</th>
                      <th>Age</th>
                      <th>Phone</th>
                      <th>Medical History</th>
                      <th>Medical Record</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {family.map(member => (
                      <tr key={member.id}>
                        <td >{member.name}</td>
                        <td>
                          <span className="badge bg-primary rounded-pill">{member.relation}</span>
                        </td>
                        <td>{member.gender || '—'}</td>
                        <td>{member.age || '—'}</td>
                        <td>{member.phone_number || '—'}</td>
                        <td>
                          {member.medical_history ? (
                            <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={member.medical_history}>
                              {member.medical_history.substring(0, 50)}...
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="text-center align-middle" >
                          {member.medical_record ? (
                            <a
                              href={getMedicalRecordUrl(member.medical_record)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary rounded-square px-2 py-1"
                              title="View Medical Record"
                            >
                              View
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm action-btn-book"
                              onClick={() => {
                                setSelectedMember(member);
                                setShowBookingModal(true);
                              }}
                              title="Book Appointment"
                            >
                              Book
                            </button>

                            <button
                              className="btn btn-sm action-btn-edit"
                              onClick={() => handleEdit(member)}
                              title="Edit Member"
                            >
                              Edit
                            </button>

                            <button
                              className="btn btn-sm action-btn-delete"
                              onClick={() => handleDeleteClick(member.id)}
                              title="Delete Member"
                            >
                              Delete
                            </button>
                          </div>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ADD FAMILY MEMBER MODAL */}
      {showAddModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Add Family Member</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleAddFamilyMember}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Name *</label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Relation *</label>
                      <FilterSelect
                        value={formData.relation}
                        onChange={(relation) => setFormData({ ...formData, relation })}
                        options={RELATION_OPTIONS}
                        placeholder="Select Relation"
                        aria-label="Relation"
                        variant="bordered"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Gender *</label>
                      <FilterSelect
                        value={formData.gender}
                        onChange={(gender) => setFormData({ ...formData, gender })}
                        options={GENDER_OPTIONS}
                        placeholder="Select Gender"
                        aria-label="Gender"
                        variant="bordered"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Age *</label>
                      <input
                        type="number"
                        className="form-control rounded-3"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        min="0"
                        max="150"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control rounded-3"
                        value={formData.phone_number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setFormData({ ...formData, phone_number: value });
                        }}
                        maxLength={10}
                        pattern="[0-9]{10}"
                        inputMode="numeric"
                      />
                      <small className="text-muted">10 digits only</small>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold small">Medical History</label>
                      <textarea
                        className="form-control rounded-3"
                        rows="3"
                        value={formData.medical_history}
                        onChange={(e) => setFormData({ ...formData, medical_history: e.target.value })}
                        placeholder="Enter any medical history or conditions..."
                      ></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold small">Medical Record (File)</label>
                      <input
                        type="file"
                        className="form-control rounded-3"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => setFormData({ ...formData, medical_record: e.target.files[0] })}
                      />
                      <small className="text-muted">PDF, DOC, DOCX, JPG, PNG</small>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mt-4">
                    <button type="submit" className="btn btn-primary rounded-pill px-4">
                      <i className="bi bi-check-lg me-2"></i>Add Member
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={() => setShowAddModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT FAMILY MEMBER MODAL */}
      {showEditModal && editingMember && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Edit Family Member</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowEditModal(false);
                  setEditingMember(null);
                  setFormData({
                    name: '',
                    relation: '',
                    gender: '',
                    age: '',
                    phone_number: '',
                    medical_history: '',
                    medical_record: null
                  });
                }}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleUpdateFamilyMember}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Name *</label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Relation *</label>
                      <FilterSelect
                        value={formData.relation}
                        onChange={(relation) => setFormData({ ...formData, relation })}
                        options={RELATION_OPTIONS}
                        placeholder="Select Relation"
                        aria-label="Relation"
                        variant="bordered"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Gender *</label>
                      <FilterSelect
                        value={formData.gender}
                        onChange={(gender) => setFormData({ ...formData, gender })}
                        options={GENDER_OPTIONS}
                        placeholder="Select Gender"
                        aria-label="Gender"
                        variant="bordered"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Age *</label>
                      <input
                        type="number"
                        className="form-control rounded-3"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        min="0"
                        max="150"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold small">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control rounded-3"
                        value={formData.phone_number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setFormData({ ...formData, phone_number: value });
                        }}
                        maxLength={10}
                        pattern="[0-9]{10}"
                        inputMode="numeric"
                      />
                      <small className="text-muted">10 digits only</small>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold small">Medical History</label>
                      <textarea
                        className="form-control rounded-3"
                        rows="3"
                        value={formData.medical_history}
                        onChange={(e) => setFormData({ ...formData, medical_history: e.target.value })}
                        placeholder="Enter any medical history or conditions..."
                      ></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold small">Medical Record (File)</label>
                      {editingMember.medical_record && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Current: </small>
                          <a
                            href={getMedicalRecordUrl(editingMember.medical_record)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-info"
                          >
                            <i className="bi bi-file-earmark-medical me-1"></i>View Current Record
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control rounded-3"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => setFormData({ ...formData, medical_record: e.target.files[0] })}
                      />
                      <small className="text-muted">Leave empty to keep current file. PDF, DOC, DOCX, JPG, PNG</small>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mt-4">
                    <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-2"></i>Update Member
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingMember(null);
                        setFormData({
                          name: '',
                          relation: '',
                          gender: '',
                          age: '',
                          phone_number: '',
                          medical_history: '',
                          medical_record: null
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BOOKING MODAL */}
      {showBookingModal && selectedMember && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">
                  Book Appointment for {selectedMember.name}
                  {selectedMember.relation ? ` (${selectedMember.relation})` : ''}
                </h5>
                <button type="button" className="btn-close" onClick={resetBookingState}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4">
                  <label className="form-label fw-bold small">Select Doctor</label>
                  <FilterSelect
                    value={selectedDoctor != null ? String(selectedDoctor) : ''}
                    onChange={(val) => handleSelectDoctor(val)}
                    options={doctors.map((doctor) => ({
                      value: String(doctor.id),
                      label: `${doctor.name} - ${doctor.specialization}`,
                    }))}
                    placeholder="Choose a doctor..."
                    aria-label="Select Doctor"
                    variant="bordered"
                  />
                </div>

                {selectedDoctor && slots.length > 0 && (
                  <div className="mb-4">
                    <label className="form-label fw-bold small">Select Available Slot</label>
                    <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {slots.map(slot => (
                        <button
                          key={slot.id}
                          type="button"
                          className={`list-group-item list-group-item-action ${selectedSlot?.id === slot.id ? 'active' : ''}`}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{slot.date || slot.date_from}</strong>
                              <br />
                              <small>{slot.start_time} - {slot.end_time}</small>
                              {slot.day_of_week && <span className="badge bg-light text-dark ms-2">{slot.day_of_week}</span>}
                            </div>
                            <i className={`bi ${selectedSlot?.id === slot.id ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedDoctor && slots.length === 0 && (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    No available slots for this doctor at the moment.
                  </div>
                )}

                <div className="d-flex gap-2 mt-4">
                  <button
                    className="btn btn-primary rounded-pill px-4"
                    onClick={handleBookAppointment}
                    disabled={!selectedSlot}
                  >
                    <i className="bi bi-arrow-right-circle me-2"></i>Continue
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={resetBookingState}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health notes & reports — same step as self booking */}
      <AppointmentNotesModal
        show={showNotesModal}
        onClose={() => {
          setShowNotesModal(false);
          setShowBookingModal(true);
        }}
        onProceed={handleNotesProceed}
      />

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowDeleteConfirm(false);
                  setMemberToDelete(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-0">
                  Are you sure you want to delete this family member? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setMemberToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={handleDeleteConfirm}
                >
                  <i className="bi bi-trash me-2"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserFamily;
